import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup
} from '@angular/forms';
import { Payload, RfqTabLegs } from '@approot/models';
import {
  BidMidAsk,
  ExecutionContextType,
  RfqDetails,
  VolBasedRfqOptionPriceV2Result
} from '@approot/services';
import { Utilities, sum } from '@approot/utilities';
import { ProviderQuote, ProviderQuoteEvent } from '../provider-quote.model';
import {
  AllPremiumBasedRisksRecords,
  AllVolBasedRisksRecords,
  CashPremiumInfo,
  FormDef,
  PremiumBasedRiskKeys,
  PremiumCash,
  PremiumInStyle,
  PremiumInStyleCurrentlyQuoted,
  PremiumInStyleInfo,
  PriceResult,
  PriceStreamingState,
  VolBasedRiskKeys
} from './provider-quote-types';

const { cleanFormattedNumeric, formatNumber } = Utilities;
export function createVolsFormArray(
  name: string,
  size: number,
  form: FormGroup,
  fb: FormBuilder,
  priceStreamingState: PriceStreamingState
) {
  if ((form.get(name) as FormArray).length === size) {
    return;
  }
  const arr = fb.array(Array.from({ length: size }, () => new FormControl('')));
  if (priceStreamingState === 'STREAMING') {
    arr.disable();
  }
  form.setControl(name, arr);
}

export function getPricePayload(
  legs: RfqTabLegs[],
  premiumCCY: string,
  executionContextType: ExecutionContextType,
  rfqDetails: RfqDetails
): Payload {
  const products = legs.map(leg => leg.leg.leg.preSetPosition);
  return {
    task: {
      taskType: 'pricePortfolio',
      reportingCurrency: premiumCCY,
      taskParameters: {
        output: ['Price', 'Gamma', 'Vega', 'SpotDelta']
      }
    },
    streamParameters: {
      duration: 900,
      refreshFrequency: 20,
      deltaRefreshFrequency: 1
    },
    executionContext: executionContextType,
    products,
    medusaRequest: rfqDetails?.medusaResponse.result
  };
}

export function calculatePremiumBasedRisks(
  r: VolBasedRfqOptionPriceV2Result
): AllPremiumBasedRisksRecords {
  const risks: AllPremiumBasedRisksRecords = {};

  PremiumBasedRiskKeys.forEach(key => {
    risks[key] = r.results.resultPerLeg.map(leg => leg[key]).filter(x => !!x);
    const total = sum(risks[key]);
    // Needs to be an array to stop template from complaining about typing
    risks[`${key}Total`] = key === 'spotDeltaInPercent' ? null : total;
  });
  return risks;
}

export function calculateVolBasedRisks(
  r: PriceResult,
  ccyPair: string
): AllVolBasedRisksRecords {
  const risks: AllVolBasedRisksRecords = {};

  VolBasedRiskKeys.forEach(key => {
    if (r.portfolioResults[key]) {
      risks[key] = r.resultPerLeg
        .map(leg => leg[key]?.[ccyPair])
        .filter(x => !!x);
      const sum1 = r.portfolioResults[key][ccyPair];
      const sum2 = sum(risks[key]);
      if (
        risks[key].length &&
        sum1 != null &&
        sum1?.toFixed(2) !== sum2?.toFixed(2)
      ) {
        console.warn(
          'Risk calculation error.  Totals mismatch',
          key,
          sum1?.toFixed(2),
          sum2?.toFixed(2)
        );
      }
      // Needs to be an array to stop template from complaining about typing
      risks[`${key}Total`] = sum1;
    } else {
      risks[`${key}Total`] = null;
    }
  });
  return risks;
}

export function calculatePremiumInStyle(
  side: 'bid' | 'ask',
  priceResult: VolBasedRfqOptionPriceV2Result,
  manualPremiumValue?: string
): PremiumInStyle {
  const premiumValue =
    priceResult.premiumValue === 'Spot' ? 'spotPremium' : 'forwardPremium';
  const premiumAmount = Math.abs(
    +(manualPremiumValue != null
      ? cleanFormattedNumeric(manualPremiumValue)
      : priceResult.portfolioResult[premiumValue][side][
          priceResult.premiumCurrency
        ])
  );
  const base = priceResult.cross.slice(0, 3);
  const term = priceResult.cross.slice(3, 6);
  const leg0 = priceResult.products[0];
  if (base === leg0.faceCurrency) {
    if (priceResult.premiumCurrency === base) {
      const value = premiumAmount / priceResult.products[0].notional;
      return {
        value,
        format: 'percent'
      };
    } else if (priceResult.premiumCurrency === term) {
      const value =
        (premiumAmount / priceResult.products[0].notional) *
        priceResult.forwardPointsMultiplier;
      return { value, format: 'pips' };
    }
  } else if (leg0.faceCurrency === term) {
    if (priceResult.premiumCurrency === term) {
      const value = premiumAmount / priceResult.products[0].counterNotional;
      return { value, format: 'percent' };
    }
  }
  return null;
}

export function calculatePremiumAmount(
  priceResult: VolBasedRfqOptionPriceV2Result,
  manualPremiumValue: string
): number {
  const premiumAmount = cleanFormattedNumeric(manualPremiumValue);
  const base = priceResult.cross.slice(0, 3);
  const term = priceResult.cross.slice(3, 6);
  const leg0 = priceResult.products[0];
  if (base === leg0.faceCurrency) {
    if (priceResult.premiumCurrency === base) {
      return (premiumAmount * priceResult.products[0].notional) / 100;
    } else if (priceResult.premiumCurrency === term) {
      return (
        (premiumAmount * priceResult.products[0].notional) /
        priceResult.forwardPointsMultiplier
      );
    }
  } else if (leg0.faceCurrency === term) {
    if (priceResult.premiumCurrency === term) {
      return (premiumAmount * priceResult.products[0].counterNotional) / 100;
    }
  }
  return null;
}

export function setVendorOptionPremium(
  result: VolBasedRfqOptionPriceV2Result,
  form: FormGroup<FormDef>,
  premiumInStyleInfo: PremiumInStyleInfo
): void {
  ['bid', 'offer'].forEach((side: 'offer' | 'bid') => {
    const premiumInStyle = calculatePremiumInStyle(
      side === 'bid' ? side : 'ask',
      result
    );
    premiumInStyleInfo.format[side] = premiumInStyle.format;
    const formattedValue = updatePremiumInStyleValue(
      premiumInStyleInfo.format[side],
      premiumInStyle.value
    );
    premiumInStyleInfo.latestValue[side] = formattedValue;
    const ctrl = form.get(
      side === 'bid' ? 'bidPremiumInStyle' : 'offerPremiumInStyle'
    );
    ctrl.enable();
    if (premiumInStyleInfo.liveValue[side]) {
      ctrl.setValue(formattedValue);
    }
  });
}

export function premiumInStyleCurrentlyQuoted(
  quoteEvent: ProviderQuoteEvent,
  priceResult: VolBasedRfqOptionPriceV2Result
): PremiumInStyleCurrentlyQuoted {
  if (!quoteEvent.quote || quoteEvent.quote.premiumAmountOffer == null) {
    return null;
  }
  const currentlyQuotingInfo = {
    bid: null,
    offer: null
  };

  ['bid', 'offer'].forEach(side => {
    if (
      (side === 'bid'
        ? quoteEvent.quote.premiumAmountBid
        : quoteEvent.quote.premiumAmountOffer) != null
    ) {
      const hydraSide = side === 'bid' ? side : 'ask';
      const premiumInStyle = calculatePremiumInStyle(
        hydraSide,
        priceResult,
        (side === 'bid'
          ? quoteEvent.quote.premiumAmountBid
          : quoteEvent.quote.premiumAmountOffer
        ).toString()
      );

      currentlyQuotingInfo[side] = {
        value: updatePremiumInStyleValue(
          premiumInStyle.format,
          premiumInStyle.value
        ),
        format: premiumInStyle.format
      };
    }
  });
  return currentlyQuotingInfo;
}

export function updatePremiumInStyleValue(
  format: string,
  value: number
): string {
  switch (format) {
    case 'pips':
      return value.toFixed(2);
    case 'percent':
      return (value * 100).toFixed(4);
    default:
      return value.toString();
  }
}

export function setCashPremiums(
  bidCashPremium: PremiumCash,
  offerCashPremium: PremiumCash,
  cashPremiumOffer: AbstractControl,
  cashPremiumBid: AbstractControl,
  cashPremiumInfo: CashPremiumInfo,
  isPremiumBasedVanillasWithSpotFeed: boolean,
  volBasedRfqOptionPriceResult: VolBasedRfqOptionPriceV2Result
): void {
  if (cashPremiumInfo.liveValue.bid) {
    cashPremiumInfo.label.bid = bidCashPremium.label;
    cashPremiumInfo.direction.bid = bidCashPremium.direction;
    const formattedValue = formatNumber(bidCashPremium.value, 2);
    cashPremiumInfo.latestValue.bid = formattedValue;
    cashPremiumBid.setValue(formattedValue);
  }

  if (cashPremiumInfo.liveValue.offer) {
    cashPremiumInfo.label.offer = offerCashPremium.label;
    cashPremiumInfo.direction.offer = offerCashPremium.direction;
    const formattedValue = formatNumber(offerCashPremium.value, 2);
    cashPremiumInfo.latestValue.offer = formattedValue;
    cashPremiumOffer.setValue(formattedValue);
  }

  if (isPremiumBasedVanillasWithSpotFeed && volBasedRfqOptionPriceResult) {
    cashPremiumBid.enable();
    cashPremiumOffer.enable();
  }
}

export function legPremiumAmountBid(
  isVolBased: boolean,
  volBasedRfqOptionPriceResult: VolBasedRfqOptionPriceV2Result,
  premiumCCY: string,
  quote: ProviderQuote,
  legIndex: number,
  premiumAmountValues: BidMidAsk,
  vendorBid: number,
  isLiveValue: boolean
): number {
  if (!isLiveValue && quote.legs.length === 1) {
    return vendorBid;
  }
  if (isVolBased || !volBasedRfqOptionPriceResult) {
    return legIndex === 0 ? quote.premiumAmountBid : 0;
  } else {
    return quote.legs[legIndex].inverted
      ? -premiumAmountValues.ask[premiumCCY]
      : premiumAmountValues.bid[premiumCCY];
  }
}

export function legPremiumAmountOffer(
  isVolBased: boolean,
  volBasedRfqOptionPriceResult: VolBasedRfqOptionPriceV2Result,
  premiumCCY: string,
  quote: ProviderQuote,
  legIndex: number,
  premiumAmountValues: BidMidAsk,
  vendorOffer: number,
  isLiveValue: boolean
): number {
  if (!isLiveValue && quote.legs.length === 1) {
    return vendorOffer;
  }
  if (isVolBased || !volBasedRfqOptionPriceResult) {
    return legIndex === 0 ? quote.premiumAmountOffer : 0;
  } else {
    return quote.legs[legIndex].inverted
      ? -premiumAmountValues.bid[premiumCCY]
      : premiumAmountValues.ask[premiumCCY];
  }
}
